/**
 * PermissionGuard 순수 로직 — DOM/React 의존 없이 권한 검증.
 * 컴포넌트와 분리해 node 테스트로 단위 검증 가능.
 */

import type { UserPermissions } from '@/shared/types/user';

export type PermissionType =
  | 'goals'
  | 'goals.company'
  | 'goals.departments'
  | 'goals.personal'
  | 'fns'
  | 'fnsPaymentStatus'
  | 'projects'
  | 'projectNumbers'
  | 'approval'
  | 'approval.create'
  | 'board'
  | 'board.write.noticeLibrary'
  | 'board.write.diTalk'
  | 'board.manage'
  | 'hrup';

/** actions 에 특정 키가 포함되는지 (unconditional + conditional 모두 검사) */
const hasAction = (permissions: UserPermissions, key: string): boolean => {
  const actions = permissions.project?.actions;
  if (!actions) return false;
  return (
    (actions.unconditional as readonly string[]).includes(key) ||
    (actions.conditional as readonly string[]).includes(key)
  );
};

/** hrup.* prefix 인 액션이 하나라도 있는지 */
const hasAnyHrupAction = (permissions: UserPermissions): boolean => {
  const actions = permissions.project?.actions;
  if (!actions) return false;
  const all: readonly string[] = [...actions.unconditional, ...actions.conditional];
  return all.some((a) => a.startsWith('hrup.'));
};

export const checkPermission = (
  permission: PermissionType,
  permissions: UserPermissions,
): boolean => {
  const menus = permissions.menus;

  switch (permission) {
    case 'goals': {
      const goals = menus.goals;
      if (!goals) return false;
      return goals.company || goals.departments || goals.personal;
    }
    case 'goals.company':
      return menus.goals?.company === true;
    case 'goals.departments':
      return menus.goals?.departments === true;
    case 'goals.personal':
      return menus.goals?.personal === true;
    case 'fns': {
      const fns = menus.fns;
      if (!fns) return false;
      return fns.accounting || fns.goals || fns.paymentStatus;
    }
    case 'fnsPaymentStatus':
      return menus.fns?.paymentStatus === true;
    case 'projects':
      return menus.projects?.list === true;
    case 'projectNumbers':
      return menus.projects?.numbers === true;
    case 'approval': {
      const approval = menus.approval;
      if (!approval) return false;
      const docs = approval.documents;
      return approval.home || approval.pending || (docs != null && (docs.temporary || docs.all));
    }
    case 'approval.create':
      // 기안 작성 행위 권한. /approval/draft/* 라우트 전용.
      return hasAction(permissions, 'approval.write.document');
    case 'board':
      return menus.board === true;
    case 'board.write.noticeLibrary':
      return hasAction(permissions, 'board.write.noticeLibrary');
    case 'board.write.diTalk':
      return hasAction(permissions, 'board.write.diTalk');
    case 'board.manage':
      // 게시글 관리(타인 글 포함 삭제 등) 행위 권한.
      return hasAction(permissions, 'board.manage');
    case 'hrup':
      // 서브 메뉴 권한 단일 관리 결정(#997): hrup.* action 하나라도 있으면 메뉴 진입 + 모든 라우트 통과.
      return hasAnyHrupAction(permissions);
    default:
      return false;
  }
};

export const hasApprovalCreatePermission = (permissions: UserPermissions): boolean =>
  hasAction(permissions, 'approval.write.document');

/** 감사 로그 조회 권한 (`/api/audit-logs` endpoint 가드) */
export const hasAuditReadPermission = (permissions: UserPermissions): boolean =>
  hasAction(permissions, 'audit.read');

/** 공식 일정 관리 권한 — 등록/수정/삭제 (`/api/official-schedules` CUD endpoint 가드) */
export const hasOfficialScheduleManagePermission = (permissions: UserPermissions): boolean =>
  hasAction(permissions, 'calendar.manage.officialSchedule');

/** 프로젝트 기여도 조회 권한 (`/api/sales/goals/financial-management` endpoint 가드) */
export const hasProjectContributionReadPermission = (permissions: UserPermissions): boolean =>
  hasAction(permissions, 'sales.read.projectContribution');

/** 인사관리 조직 관리 권한 — 인원/부서 정보 수정, 퇴사 처리 등 (PEO-001 인원 상세 팝업 수정/퇴사 처리 버튼 가드) */
export const hasHrupManageOrgPermission = (permissions: UserPermissions): boolean =>
  hasAction(permissions, 'hrup.manage.org');
